/**
 * Defensive normalization for pool responses.
 *
 * Pool responses may include nested objects, JSON-string blobs, or omit fields
 * entirely. We collapse those variants into the stable shapes documented in
 * `./types.ts`, then strip product-enrichment placeholders we can't resolve in
 * the browser POC and substitute date macros from local time.
 *
 * Yoto parity:
 * - `renderGalleryForEcom` substitutes `{year} {month} {day}` macros in
 *   `content/outro` and per-item `title/description/description_2`.
 * - It also substitutes per-product placeholders (`{price} {listPrice}
 *   {reviews} {rating} {discount}`) and brackets (`[al]/[/al]`,
 *   `[discount]/[/discount]`, `[no-discount]/[/no-discount]`). The POC has no
 *   product enrichment data, so we strip them rather than leaving raw markup.
 */

import type { SdkPost, SdkPostItem, SdkWebsite } from './types';

/* -------------------------------------------------------------------------- */
/* Primitive helpers                                                           */
/* -------------------------------------------------------------------------- */

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function asNumber(value: unknown, fallback?: number): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') return true;
    if (s === '0' || s === 'false' || s === 'no') return false;
  }
  return fallback;
}

function asIdLike(value: unknown, fallback: number | string = 0): number | string {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value !== '') return value;
  return fallback;
}

/**
 * Yoto stores `data` as either a nested object or a JSON-encoded string. Both
 * shapes are valid per the legacy CMS path; coerce to a plain object.
 */
function asDataBag(value: unknown): Record<string, unknown> {
  if (isObject(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(value);
      if (isObject(parsed)) return parsed;
    } catch {
      /* fall through to empty */
    }
  }
  return {};
}

/* -------------------------------------------------------------------------- */
/* String macros                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Substitute `{year}/{month}/{day}` (case-insensitive, mirrors
 * renderGalleryForEcom). Empty input is returned unchanged.
 */
export function substituteDateMacros(input: string, now: Date = new Date()): string {
  if (!input) return input;
  const year = String(now.getFullYear());
  // English long-form, matches PHP `now->format('F')` / `'l'`.
  const month = now.toLocaleString('en-US', { month: 'long' });
  const day = now.toLocaleString('en-US', { weekday: 'long' });
  return input
    .replace(/\{year\}/gi, year)
    .replace(/\{month\}/gi, month)
    .replace(/\{day\}/gi, day);
}

/**
 * Strip product-enrichment placeholders we cannot resolve without product data.
 * - `[al]...[/al]` collapses to its inner text (Yoto wraps it in an anchor when
 *   `has_ecom_link`; we just unwrap to plain text).
 * - `[discount]...[/discount]` and `[no-discount]...[/no-discount]` blocks are
 *   removed entirely — without product data we cannot tell which branch wins.
 * - `{price} {listPrice} {reviews} {rating} {discount}` placeholders strip to ''.
 */
export function stripProductPlaceholders(input: string): string {
  if (!input) return input;
  let out = input;
  out = out.replace(/\[discount\][\s\S]*?\[\/discount\]/gi, '');
  out = out.replace(/\[no-discount\][\s\S]*?\[\/no-discount\]/gi, '');
  out = out.replace(/\[al\]([\s\S]*?)\[\/al\]/gi, '$1');
  out = out.replace(/\{(price|listPrice|reviews|rating|discount)\}/gi, '');
  return out;
}

/** Convenience: macros + placeholder strip in the order Yoto applies them. */
export function applyTextMacros(input: string, now: Date = new Date()): string {
  return stripProductPlaceholders(substituteDateMacros(input, now));
}

/* -------------------------------------------------------------------------- */
/* Website                                                                     */
/* -------------------------------------------------------------------------- */

export function normalizeWebsite(raw: unknown): SdkWebsite {
  if (!isObject(raw)) {
    throw new Error('pool website: payload is not an object');
  }
  const websiteNode = isObject(raw.website) ? raw.website : raw;
  const domain = asString((websiteNode as Record<string, unknown>).domain);
  const geoNode = isObject(raw.geo) ? raw.geo : {};
  const cmpNode = isObject(raw.cmp) ? raw.cmp : {};

  if (!domain) {
    throw new Error('pool website: missing website.domain');
  }

  const country = asString(geoNode.country).trim().toUpperCase() || 'UNKNOWN';
  const region = asString(geoNode.region).trim();
  const cc_bmi = asNumber(geoNode.cc_bmi ?? geoNode.ccBMI);

  const geo: SdkWebsite['geo'] = { country };
  if (region) geo.region = region;
  if (cc_bmi !== undefined) geo.cc_bmi = cc_bmi;

  return {
    domain,
    geo,
    cmp: {
      required: asBoolean(cmpNode.required),
      ccpa_applicable: asBoolean(cmpNode.ccpa_applicable),
    },
    logo_url: asString((websiteNode as Record<string, unknown>).logo_url) || undefined,
    fonts_params: asString((websiteNode as Record<string, unknown>).fonts_params) || undefined,
  };
}

/* -------------------------------------------------------------------------- */
/* Post                                                                        */
/* -------------------------------------------------------------------------- */

export interface NormalizePostOptions {
  now?: Date;
}

export function normalizePost(raw: unknown, opts: NormalizePostOptions = {}): SdkPost {
  if (!isObject(raw)) {
    throw new Error('pool post: payload is not an object');
  }
  const now = opts.now ?? new Date();

  const post: Record<string, unknown> = { ...raw };
  const data = asDataBag(raw.data);

  post.id = asIdLike(raw.id, '');
  post.title = asString(raw.title);
  post.browser_title = asString(raw.browser_title) || (post.title as string);
  post.locale = asString(raw.locale).trim().toLowerCase();
  post.locales = Array.isArray(raw.locales)
    ? raw.locales.map((locale) => asString(locale)).filter(Boolean)
    : [];
  post.data = data;
  post.custom_content = asDataBag(raw.custom_content);

  if (typeof raw.content === 'string') {
    post.content = applyTextMacros(raw.content, now);
  }
  if (typeof raw.outro === 'string') {
    post.outro = applyTextMacros(raw.outro, now);
  }
  if (data.source !== undefined || raw.source !== undefined) {
    post.source = asString(data.source ?? raw.source);
  }
  if (data.source_url !== undefined || raw.source_url !== undefined) {
    post.source_url = asString(data.source_url ?? raw.source_url);
  }
  if (data.ecom_external_link !== undefined || raw.ecom_external_link !== undefined) {
    post.ecom_external_link = asString(data.ecom_external_link ?? raw.ecom_external_link);
  }
  if (Array.isArray(raw.items)) {
    post.items = raw.items.map((item, idx) => normalizePostItem(item, now, idx + 1));
  }

  return post as SdkPost;
}

function itemNumberFromPool(obj: Record<string, unknown>, data: Record<string, unknown>, fallback: number): number {
  const n = asNumber(
    obj.item ?? obj.data_item ?? obj.gallery_item_number ?? data.item ?? data.data_item,
  );
  return n !== undefined && Number.isFinite(n) ? n : fallback;
}

function normalizePostItem(raw: unknown, now: Date, position1Based: number): SdkPostItem {
  const obj = isObject(raw) ? raw : {};
  const data = asDataBag(obj.data);
  const item_number = itemNumberFromPool(obj, data, position1Based);
  const item: Record<string, unknown> = { ...obj };

  item.id = asIdLike(obj.id, '');
  item.item_number = item_number;
  item.data = data;

  if (typeof obj.title === 'string') item.title = applyTextMacros(obj.title, now);
  if (typeof obj.description === 'string') item.description = applyTextMacros(obj.description, now);
  if (typeof obj.description_2 === 'string') {
    item.description_2 = applyTextMacros(obj.description_2, now);
  }

  const mediaUrl = obj.media_url ?? obj.media ?? data.media_url ?? data.media;
  if (mediaUrl !== undefined) item.media_url = asString(mediaUrl);

  const mediaType = obj.media_type ?? data.media_type;
  if (mediaType !== undefined) item.media_type = asString(mediaType, 'image');

  const ecomProductId = data.ecom_product_id ?? obj.ecom_product_id;
  if (ecomProductId !== undefined) item.ecom_product_id = asString(ecomProductId);

  const ecomGeneralLink = data.ecom_general_link ?? obj.ecom_general_link;
  if (ecomGeneralLink !== undefined) item.ecom_general_link = asString(ecomGeneralLink);

  const ecomProductMarket = data.ecom_product_market ?? obj.ecom_product_market;
  if (ecomProductMarket !== undefined) {
    item.ecom_product_market = asString(ecomProductMarket, 'amazon_us');
  }

  if (data.source !== undefined || obj.source !== undefined) {
    item.source = asString(data.source ?? obj.source);
  }
  if (data.source_url !== undefined || obj.source_url !== undefined) {
    item.source_url = asString(data.source_url ?? obj.source_url);
  }

  return item as unknown as SdkPostItem;
}
